// src/Header/Header.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { apiFetch, clearAccessToken } from "../api";
import { useTheme } from "../Profil/useTheme";
import { FiUser } from "react-icons/fi";
import "./Header.css";

function normalizirajUporabnika(data) {
    return {
        id: data?.id || "",
        email: data?.email || "",
        firstName: data?.firstName || "",
        lastName: data?.lastName || "",
        fullName: data?.fullName || "",
        address: data?.address || "",
        gsm: data?.gsm || "",
        idZaDdv: data?.idZaDdv || "",
        maticnaStevilka: data?.maticnaStevilka || "",
        transakcijskiRacunStevilka: data?.transakcijskiRacunStevilka || "",
    };
}

function prikazi(value) {
    const v = String(value || "").trim();
    return v || "—";
}

export default function Header() {
    const navigate = useNavigate();
    const location = useLocation();
    const { theme, setTheme } = useTheme();

    const userBoxRef = useRef(null);

    const [user, setUser] = useState(null);
    const [podrobnostiOdprte, setPodrobnostiOdprte] = useState(false);

    useEffect(() => {
        let active = true;

        (async () => {
            try {
                const res = await apiFetch("/api/auth/me");

                if (!active) return;

                if (!res.ok) {
                    setUser(null);
                    return;
                }

                const data = await res.json();

                if (!active) return;

                setUser(normalizirajUporabnika(data));
            } catch {
                if (active) {
                    setUser(null);
                }
            }
        })();

        return () => {
            active = false;
        };
    }, [location.pathname]);

    useEffect(() => {
        if (!podrobnostiOdprte) return;

        function handleClickOutside(e) {
            if (
                userBoxRef.current &&
                !userBoxRef.current.contains(e.target)
            ) {
                setPodrobnostiOdprte(false);
            }
        }

        function handleKeyDown(e) {
            if (e.key === "Escape") {
                setPodrobnostiOdprte(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [podrobnostiOdprte]);

    const preklopiTemo = () => {
        setTheme(theme === "light" ? "dark" : "light");
    };

    const displayName =
        (user?.fullName && user.fullName.trim()) ||
        user?.email ||
        "Nalaganje…";

    const userRows = useMemo(
        () => [
            ["E-pošta", user?.email],
            ["Ime", user?.firstName],
            ["Priimek", user?.lastName],
            ["Naslov", user?.address],
            ["GSM", user?.gsm],
            ["ID za DDV", user?.idZaDdv],
            ["Matična številka", user?.maticnaStevilka],
            ["TRR", user?.transakcijskiRacunStevilka],
        ],
        [user]
    );

    return (
        <div className="header-root">
            <button
                type="button"
                className="header-logoLink"
                onClick={() => navigate("/")}
                aria-label="Pojdi na domačo stran"
                title="Domov"
            >
                <img
                    src="/izvrsitelj.png"
                    alt="IZVRŠITELJ"
                    loading="eager"
                    className="header-logo"
                />
            </button>

            <div className="header-info" ref={userBoxRef}>
                <div className="header-label">
                    <FiUser className="header-labelIcon" aria-hidden="true" />
                    <span>Prijavljeni izvršitelj</span>
                </div>

                <button
                    type="button"
                    className="header-userButton"
                    onClick={() => setPodrobnostiOdprte((v) => !v)}
                    disabled={!user}
                    aria-expanded={podrobnostiOdprte}
                    aria-label="Prikaži podatke prijavljenega uporabnika"
                    title={user?.email || displayName}
                >
                    <span className="header-statusDot" aria-hidden="true" />
                    <span className="header-userButtonText">{displayName}</span>
                    <span className="header-userButtonArrow" aria-hidden="true">
                        ▾
                    </span>
                </button>

                {podrobnostiOdprte && user && (
                    <div className="header-userPanel" role="dialog">
                        <div className="header-userPanelHead">
                            <strong>Podatki</strong>
                            <button
                                type="button"
                                className="header-userPanelClose"
                                onClick={() => setPodrobnostiOdprte(false)}
                                aria-label="Zapri podatke uporabnika"
                            >
                                ×
                            </button>
                        </div>

                        <div className="header-userPanelRows">
                            {userRows.map(([label, value]) => (
                                <div className="header-userPanelRow" key={label}>
                                    <span className="header-userPanelLabel">
                                        {label}
                                    </span>
                                    <span className="header-userPanelValue">
                                        {prikazi(value)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <div className="header-spacer" />

            <button
                type="button"
                className="header-themeToggle"
                data-mode={theme}
                onClick={preklopiTemo}
                aria-label={
                    theme === "light"
                        ? "Preklopi na temno temo"
                        : "Preklopi na svetlo temo"
                }
                aria-pressed={theme === "dark"}
            >
                <span className="header-themeToggle__rail" aria-hidden="true">
                    <span className="header-themeToggle__icon header-themeToggle__icon--sun">
                        ☀
                    </span>
                    <span className="header-themeToggle__icon header-themeToggle__icon--moon">
                        🌙
                    </span>
                </span>
                <span className="header-themeToggle__thumb">
                    <span className="header-themeToggle__thumbIcon">
                        {theme === "light" ? "☀" : "🌙"}
                    </span>
                </span>
            </button>

            <button
                type="button"
                className="header-logout btn-nazaj"
                onClick={() => {
                    clearAccessToken(true);
                    navigate("/vpis", { replace: true });
                }}
            >
                <span className="btn-nazaj__text">Odjava</span>
            </button>
        </div>
    );
}